/**
 * Fallback cuando Web Audio sigue `suspended` (p. ej. auto-llamada vía Realtime sin gesto).
 * `<audio>` + WAV en memoria a veces se comporta distinto al `AudioContext` compartido con DTMF.
 */

type FallbackKind = 'pe_long' | 'us_short';

let cachedPeLong: string | null = null;
let cachedUsShort: string | null = null;
let audioEl: HTMLAudioElement | null = null;

function floatTo16BitPCM(output: DataView, offset: number, input: Float32Array): void {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]!));
    output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
}

function encodeWav(samples: Float32Array, sampleRate: number): string {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (o: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, samples.length * 2, true);
  floatTo16BitPCM(view, 44, samples);
  const u8 = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]!);
  return `data:audio/wav;base64,${btoa(bin)}`;
}

function buildPeLongTuuDataUrl(): string {
  const sr = 22050;
  const dur = 1.08;
  const freq = 425;
  const n = Math.floor(sr * dur);
  const out = new Float32Array(n);
  const vol = 0.14;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const env = Math.sin(Math.PI * (i / Math.max(1, n - 1)));
    out[i] = vol * Math.sin(2 * Math.PI * freq * t) * env;
  }
  return encodeWav(out, sr);
}

function buildUsShortPulseDataUrl(): string {
  const sr = 22050;
  const dur = 0.88;
  const n = Math.floor(sr * dur);
  const out = new Float32Array(n);
  const vol = 0.11;
  for (let i = 0; i < n; i++) {
    const t = i / sr;
    const env = Math.sin(Math.PI * (i / Math.max(1, n - 1)));
    const a = Math.sin(2 * Math.PI * 440 * t);
    const b = Math.sin(2 * Math.PI * 480 * t);
    out[i] = vol * (a + b) * 0.5 * env;
  }
  return encodeWav(out, sr);
}

function dataUrlFor(kind: FallbackKind): string {
  if (kind === 'us_short') {
    if (!cachedUsShort) cachedUsShort = buildUsShortPulseDataUrl();
    return cachedUsShort;
  }
  if (!cachedPeLong) cachedPeLong = buildPeLongTuuDataUrl();
  return cachedPeLong;
}

/** Un pulso de timbrado vía elemento media (fallback). */
export function playRingbackHtmlFallbackPulse(kind: FallbackKind): void {
  if (typeof window === 'undefined') return;
  try {
    if (!audioEl) audioEl = new Audio();
    audioEl.src = dataUrlFor(kind);
    audioEl.volume = 0.42;
    void audioEl.play().catch(() => {});
  } catch {
    // ignore
  }
}
